import React, { useState, useEffect, useRef, useCallback } from 'react';
import './OrdersPage.css';
import Layout from '../../../Components/Layout/Layout';
import ReactLoading from 'react-loading';
import { usePaginatedPost } from '../../../hooks/usePaginatedPost';
import { useTelegram } from '../../../hooks/auth/useTelegram';
import api from '../../../hooks/api';
import { useLocation } from 'react-router-dom';

// وارد کردن کامپوننت‌های جدا شده
import { OrderSkeleton } from './components/OrderSkeleton';
import { OrderCard } from './components/OrderCard';
import { OrderHistoryModal } from './modals/OrderHistoryModal';
import { InvoiceModal } from './modals/InvoiceModal';
import { mapApiToUiModel } from './utils';
import config from '../../../config';

const OrdersPage = () => {
    const { ChatId } = useTelegram();
    const location = useLocation();
  const startParam = 
        location.state?.startParam || 
        sessionStorage.getItem('telegram_start_param') || 
        window.Telegram?.WebApp?.initDataUnsafe?.start_param;
        
    const [activeTab, setActiveTab] = useState(startParam === config.telegram_actions.BUYER_CANCEL_INVOICE ? 'cancelled' : 'processing');
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [isInvoiceModalLoading, setIsInvoiceModalLoading] = useState(false);

    // --- State های مودال تاریخچه گفتگو ---
    const [historyModalOpen, setHistoryModalOpen] = useState(false);
    const [historyInvoiceId, setHistoryInvoiceId] = useState(null);
    const [historyOrderFactor, setHistoryOrderFactor] = useState('');

    const [cancelledPage, setCancelledPage] = useState(1);
    const [processingPage, setProcessingPage] = useState(1);
    const [deliveredPage, setDeliveredPage] = useState(1);
    const [removedOrderIds, setRemovedOrderIds] = useState([]);

    const cancelledUrl = activeTab === 'cancelled' ? `invoice/repeal/byBuyer?page=` : null;
    const { data: cancelledDataRaw, loading: cancelledLoading, isLastPage: cancelledIsLastPage } = usePaginatedPost(cancelledUrl, null, cancelledPage);

    const processingUrl = activeTab === 'processing' ? `invoice/current/orders/byBuyer?page=` : null;
    const { data: processingDataRaw, loading: processingLoading, isLastPage: processingIsLastPage } = usePaginatedPost(processingUrl, null, processingPage);

    const deliveredUrl = activeTab === 'delivered' ? `invoice/delivery/byBuyer?page=` : null;
    const { data: deliveredDataRaw, loading: deliveredLoading, isLastPage: deliveredIsLastPage } = usePaginatedPost(deliveredUrl, null, deliveredPage);

    // محاسبه وضعیت‌های مختلف لودینگ و داده‌های نمایشی
    const isLoading = (activeTab === 'cancelled' && cancelledLoading && cancelledPage === 1) ||
        (activeTab === 'processing' && processingLoading && processingPage === 1) ||
        (activeTab === 'delivered' && deliveredLoading && deliveredPage === 1);

    const isLoadMoreLoading = (activeTab === 'cancelled' && cancelledLoading && cancelledPage > 1) ||
        (activeTab === 'processing' && processingLoading && processingPage > 1) ||
        (activeTab === 'delivered' && deliveredLoading && deliveredPage > 1);

    const isLastPage = (activeTab === 'cancelled' && cancelledIsLastPage) ||
        (activeTab === 'processing' && processingIsLastPage) ||
        (activeTab === 'delivered' && deliveredIsLastPage);

    let displayedOrders = [];
    if (activeTab === 'cancelled') {
        displayedOrders = (cancelledDataRaw || []).filter(o => !removedOrderIds.includes(o.id)).map(mapApiToUiModel);
    } else if (activeTab === 'processing') {
        displayedOrders = (processingDataRaw || []).filter(o => !removedOrderIds.includes(o.id)).map(mapApiToUiModel);
    } else if (activeTab === 'delivered') {
        displayedOrders = (deliveredDataRaw || []).filter(o => !removedOrderIds.includes(o.id)).map(mapApiToUiModel);
    }

    const handleRemoveOrder = (orderId) => { setRemovedOrderIds(prev => [...prev, orderId]); };

    const handleTabChange = (newTab) => {
        if (activeTab === newTab) return;
        setActiveTab(newTab); setRemovedOrderIds([]);
        setProcessingPage(1); setDeliveredPage(1); setCancelledPage(1);
    };

    // باز کردن مودال تاریخچه بدون نیاز به صدا زدن API در والد
    const handleShowHistory = (order) => {
        setHistoryInvoiceId(order.id);
        setHistoryOrderFactor(order.factorNumber || order.id);
        setHistoryModalOpen(true);
    };

    const handleFetchAndShowInvoice = async (order) => {
        setSelectedOrder(order);
        setIsInvoiceModalLoading(true);

        try {
            const response = await api.post('invoice/search/byFactorId', { invoice_id: order.id, business_id: order.business_id });
            if (response.data && response.data.success) {
                const fetchedInvoiceData = Array.isArray(response.data.data) ? response.data.data[0] : response.data.data;
                const trackingCode = fetchedInvoiceData.trackingCodePost || order.trackingCodePost;

                setSelectedOrder(prev => {
                    if (!prev || prev.id !== order.id) return prev;

                    const buyerFullName = `${fetchedInvoiceData.name_buyer_archive || ''} ${fetchedInvoiceData.family_buyer_archive || ''}`.trim() || fetchedInvoiceData.buyer?.name || 'نامشخص';
                    let totalConsumer = 0, totalProfit = 0, totalVat = 0, totalShippingAndPack = Number(fetchedInvoiceData.send_cost_price || 0);

                    const formattedItems = (fetchedInvoiceData.invoice_details || []).map(detail => {
                        const count = Number(detail.amount || 0), basePrice = Number(detail.price || 0);
                        const discount = Number(detail.discount_price || 0), vat = Number(detail.vat_price || 0), totalPrice = Number(detail.totalPrice || 0);
                        totalConsumer += totalPrice; totalProfit += discount; totalVat += vat; totalShippingAndPack += Number(detail.pack_cost || 0);

                        return {
                            name: detail.commodityName || 'نامشخص', count, originalPrice: basePrice,
                            ourPrice: basePrice - (discount / count), total: totalPrice - discount
                        };
                    });

                    return {
                        ...prev, date: fetchedInvoiceData.created_at, trackingCodePost: trackingCode,
                        finance_trnsactions: fetchedInvoiceData.finance_trnsactions || prev.finance_trnsactions || [],
                        invoice: {
                            seller: { name: fetchedInvoiceData.business?.title || prev.shop, phone: '', address: '' },
                            buyer: { name: buyerFullName, phone: fetchedInvoiceData.buyer?.mobile || '' },
                            items: formattedItems,
                            totals: { consumer: totalConsumer, profit: totalProfit, vat: totalVat, shipping: totalShippingAndPack }
                        }
                    };
                });
            }
        } catch (error) {
            console.error("Error fetching invoice:", error);
        } finally {
            setIsInvoiceModalLoading(false);
        }
    };

    const handleLoadMore = useCallback(() => {
        if (activeTab === 'cancelled') setCancelledPage(p => p + 1);
        if (activeTab === 'processing') setProcessingPage(p => p + 1);
        if (activeTab === 'delivered') setDeliveredPage(p => p + 1);
    }, [activeTab]);

    // آبزرور اسکرول بی‌نهایت
    const observerRef = useRef(null);

    useEffect(() => {
        if (isLoading || isLastPage) return;

        const observer = new IntersectionObserver(
            (entries) => {
                if (entries[0].isIntersecting && !isLoadMoreLoading) {
                    handleLoadMore();
                }
            },
            { threshold: 0.1, rootMargin: '150px' }
        );

        const currentTarget = observerRef.current;
        if (currentTarget) {
            observer.observe(currentTarget);
        }

        return () => {
            if (currentTarget) observer.unobserve(currentTarget);
        };
    }, [isLoading, isLastPage, isLoadMoreLoading, handleLoadMore]);

    return (
        <Layout hideNotification={true}>
            <div className="orders-page">
                <div className="status-tabs-container">
                    <button className={`tab-button ${activeTab === 'processing' ? 'active' : ''}`} onClick={() => handleTabChange('processing')}>سفارشات جاری</button>
                    <button className={`tab-button ${activeTab === 'delivered' ? 'active' : ''}`} onClick={() => handleTabChange('delivered')}>تحویل داده شده</button>
                    <button className={`tab-button ${activeTab === 'cancelled' ? 'active' : ''}`} onClick={() => handleTabChange('cancelled')}>ابطال شده</button>
                </div>

                <div className="orders-list-container">
                    {isLoading ? (
                        <><OrderSkeleton /><OrderSkeleton /><OrderSkeleton /></>
                    ) : (
                        <>
                            {displayedOrders.length > 0 ? (
                                displayedOrders.map((order, index) => (
                                    <OrderCard
                                        key={`${order.id}-${index}`}
                                        data={order}
                                        onShowDetails={handleFetchAndShowInvoice}
                                        onRemoveOrder={handleRemoveOrder}
                                        ChatId={`${ChatId}`}
                                        onShowHistory={handleShowHistory}
                                        activeTab={activeTab}
                                    />
                                ))
                            ) : (<div className="empty-state">موردی یافت نشد.</div>)}

                            {/* المان کنترل اسکرول و لودر سه نقطه */}
                            {!isLastPage && displayedOrders.length > 0 && (
                                <div ref={observerRef} style={{ width: '100%', minHeight: '30px', display: 'flex', justifyContent: 'center', alignItems: 'center', margin: '15px 0' }}>
                                    {isLoadMoreLoading && (
                                        <ReactLoading type="bubbles" color="#3b82f6" height={30} width={40} />
                                    )}
                                </div>
                            )}

                            <div style={{ height: '150px', paddingBottom: 'env(safe-area-inset-bottom)+50px', width: '100%' }}></div>
                        </>
                    )}
                    <div style={{ minHeight: '140px' }}></div>
                </div>

                {selectedOrder && (
                    <InvoiceModal order={selectedOrder} isLoading={isInvoiceModalLoading} onClose={() => { setSelectedOrder(null); setIsInvoiceModalLoading(false); }} />
                )}

                {/* مودال تاریخچه که خودش دیتای فاکتور را لود می‌کند */}
                <OrderHistoryModal
                    isOpen={historyModalOpen}
                    onClose={() => {
                        setHistoryModalOpen(false);
                        setHistoryInvoiceId(null);
                    }}
                    invoiceId={historyInvoiceId}
                    orderFactor={historyOrderFactor}
                />
            </div>
        </Layout>
    );
};

export default OrdersPage;
